import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AppLink, useAppNavigate } from "../lib/route-primitives";
import { adminSupportApi } from "../lib/api";
import {
  Loader2, LifeBuoy, ChevronRight, ArrowLeft, Save, Settings2, Send,
  Paperclip, CheckCircle, Trash2, Plus, X, Check,
} from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";

const TICKET_LIMIT = 50;
const formatTicketNumber = (n: number) => `SUP-${String(n).padStart(4, "0")}`;

interface TicketRow {
  id: string; ticket_number: number; subject: string;
  status: "open" | "in_review" | "resolved" | "closed";
  priority: string; category: string; admin_unread: number;
  organizer_email: string; organizer_name: string | null;
  wedding_slug: string | null; created_at: string;
}
interface AdminSupportMessage {
  id: string; author_type: "organizer" | "admin";
  content: string; is_internal: number;
  attachment_keys: string | null; created_at: string;
}
interface TicketDetail {
  id: string; ticket_number: number; subject: string; body: string;
  status: string; priority: string; category: string;
  admin_unread: number; organizer_unread: number;
  organizer_email: string; organizer_name: string | null;
  wedding_slug: string | null; csat_score: number | null;
  created_at: string; messages: AdminSupportMessage[];
}

// ── Badge configs ──────────────────────────────────────────────

const STATUS_CFG: Record<string, { label: string; cls: string; dot: string }> = {
  open:      { label: "Açık",        cls: "bg-blue-500/10 text-blue-400 border-blue-500/20",   dot: "bg-blue-400" },
  in_review: { label: "İnceleniyor", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", dot: "bg-amber-400" },
  resolved:  { label: "Çözüldü",    cls: "bg-green-500/10 text-green-400 border-green-500/20", dot: "bg-green-400" },
  closed:    { label: "Kapatıldı",  cls: "bg-muted text-muted-foreground border-border",        dot: "bg-muted-foreground" },
};
const PRIORITY_CFG: Record<string, { label: string; cls: string }> = {
  low:    { label: "Düşük",   cls: "bg-muted text-muted-foreground border-border" },
  normal: { label: "Normal",  cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  high:   { label: "Yüksek", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  urgent: { label: "Acil",   cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};
const CATEGORY_CFG: Record<string, string> = {
  general: "Genel", billing: "Fatura", technical: "Teknik",
  bug: "Hata", feature_request: "Öneri",
};

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_CFG[status] ?? STATUS_CFG.open;
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border", c.cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", c.dot)} />
      {c.label}
    </span>
  );
}
function PriorityBadge({ priority }: { priority: string }) {
  const c = PRIORITY_CFG[priority] ?? PRIORITY_CFG.normal;
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border", c.cls)}>
      {c.label}
    </span>
  );
}
function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr + (dateStr.includes("Z") ? "" : "Z")).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "az önce";
  if (m < 60) return `${m}dk`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}sa`;
  return `${Math.floor(h / 24)}g`;
}

// ── Ticket list page ───────────────────────────────────────────

export function AdminSupportPage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();

  const [tickets, setTickets]       = useState<TicketRow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]       = useState(false);
  const [statusFilter, setStatusFilter]   = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showSettings, setShowSettings]   = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: String(TICKET_LIMIT) });
      if (statusFilter)   p.set("status", statusFilter);
      if (priorityFilter) p.set("priority", priorityFilter);
      if (categoryFilter) p.set("category", categoryFilter);
      const res = await adminSupportApi.get<TicketRow[]>(`?${p}`);
      const rows = res.data ?? [];
      setTickets(rows);
      setHasMore(rows.length >= TICKET_LIMIT);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [statusFilter, priorityFilter, categoryFilter]);

  const fetchMore = async () => {
    if (!tickets.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams({ limit: String(TICKET_LIMIT), cursor: tickets[tickets.length - 1].created_at });
      if (statusFilter)   p.set("status", statusFilter);
      if (priorityFilter) p.set("priority", priorityFilter);
      if (categoryFilter) p.set("category", categoryFilter);
      const res = await adminSupportApi.get<TicketRow[]>(`?${p}`);
      const rows = res.data ?? [];
      setTickets((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= TICKET_LIMIT);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif italic text-3xl text-foreground">{t("adminSupport.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Organizatör destek talepleri</p>
        </div>
        <button onClick={() => setShowSettings((v) => !v)}
          className={cn("shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer",
            showSettings ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground")}>
          <Settings2 className="h-3.5 w-3.5" /> Ayarlar
        </button>
      </div>

      {showSettings && <SupportSettingsPanel />}

      {/* Status filter pills */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {["", "open", "in_review", "resolved", "closed"].map((s) => (
            <button key={s}
              onClick={() => setStatusFilter(s)}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap",
                statusFilter === s ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}>
              {s === "" ? "Tümü" : STATUS_CFG[s]?.label ?? s}
            </button>
          ))}
        </div>

        {/* Priority pills */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {["", "urgent", "high", "normal", "low"].map((p) => (
            <button key={p}
              onClick={() => setPriorityFilter(p)}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                priorityFilter === p ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}>
              {p === "" ? "Öncelik" : PRIORITY_CFG[p]?.label ?? p}
            </button>
          ))}
        </div>

        {/* Category pills */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {["", ...Object.keys(CATEGORY_CFG)].map((c) => (
            <button key={c}
              onClick={() => setCategoryFilter(c)}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                categoryFilter === c ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}>
              {c === "" ? "Kategori" : CATEGORY_CFG[c]}
            </button>
          ))}
        </div>

        {!loading && <span className="text-xs text-muted-foreground ml-auto">{tickets.length} talep{hasMore ? "+" : ""}</span>}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 rounded-2xl border border-border">
          <LifeBuoy className="h-8 w-8 text-primary/30" />
          <p className="text-sm text-muted-foreground">{t("adminSupport.noTickets")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border overflow-hidden">
            {tickets.map((tk, i) => {
              const statusCfg = STATUS_CFG[tk.status] ?? STATUS_CFG.open;
              const initial = (tk.organizer_name ?? tk.organizer_email)[0].toUpperCase();
              return (
                <div
                  key={tk.id}
                  onClick={() => navigate(paths.supportDetail(tk.id))}
                  className={cn(
                    "flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors border-l-4",
                    i > 0 && "border-t border-border",
                    tk.admin_unread === 1
                      ? "border-l-primary bg-primary/5 hover:bg-primary/8"
                      : "border-l-transparent hover:bg-primary/5"
                  )}
                >
                  {/* Avatar */}
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {initial}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                        {formatTicketNumber(tk.ticket_number)}
                      </span>
                      <StatusBadge status={tk.status} />
                      <PriorityBadge priority={tk.priority} />
                      {tk.admin_unread === 1 && (
                        <span className="h-2 w-2 rounded-full bg-primary animate-pulse" title="Okunmamış mesaj" />
                      )}
                    </div>
                    <p className="text-sm font-semibold text-foreground truncate">{tk.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {tk.organizer_name ?? tk.organizer_email}
                      {tk.wedding_slug && (
                        <span className="ml-2 font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">/{tk.wedding_slug}</span>
                      )}
                      <span className="mx-1.5">·</span>
                      {CATEGORY_CFG[tk.category] ?? tk.category}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-[10px] text-muted-foreground">{timeAgo(tk.created_at)}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button onClick={fetchMore} disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("common.loadMore")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Settings panel ─────────────────────────────────────────────

function SupportSettingsPanel() {
  const { t } = useTranslation();
  const [email, setEmail]   = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    adminSupportApi.get<{ supportEmail: string }>("/settings")
      .then((res) => setEmail(res.data?.supportEmail ?? ""))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSupportApi.patch("/settings", { supportEmail: email });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="rounded-2xl border border-border p-5 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border bg-muted/30">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("adminSupport.settingsTitle")}</p>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t("adminSupport.emailLabel")}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full max-w-sm bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg px-3 py-2 text-sm transition-[border-color,box-shadow]" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer shadow-sm shadow-primary/20">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("common.save")}
          </button>
          {saved && <span className="text-xs text-green-500 font-medium">Kaydedildi ✓</span>}
        </div>
        <CannedResponsesPanel />
      </div>
    </div>
  );
}

function CannedResponsesPanel() {
  const { t } = useTranslation();
  const [canned, setCanned] = useState<Array<{ id: string; title: string; content: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle]   = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminSupportApi.listCanned().then((r) => setCanned(r.data ?? [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      const res = await adminSupportApi.createCanned(title.trim(), content.trim());
      if (res.data) {
        setCanned((p) => [...p, { id: res.data!.id, title: title.trim(), content: content.trim() }]);
        setTitle(""); setContent(""); setShowForm(false);
      }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("support.deleteCannedConfirm"))) return;
    try { await adminSupportApi.deleteCanned(id); setCanned((p) => p.filter((c) => c.id !== id)); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{t("support.cannedResponses")}</p>
        <button onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
          <Plus className="h-3.5 w-3.5" /> {t("support.addCannedResponse")}
        </button>
      </div>
      {showForm && (
        <div className="space-y-2 p-3 rounded-xl bg-muted/30 border border-border">
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder={t("support.cannedTitle")}
            className="w-full bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg px-3 py-2 text-sm" />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3}
            placeholder={t("support.cannedContent")}
            className="w-full resize-none bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving || !title.trim() || !content.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer">
              {saving && <Loader2 className="h-3 w-3 animate-spin" />} {t("common.save")}
            </button>
            <button onClick={() => { setShowForm(false); setTitle(""); setContent(""); }}
              className="px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted cursor-pointer">
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {loading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : canned.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{t("support.cannedResponsesEmpty")}</p>
      ) : (
        <div className="space-y-1.5">
          {canned.map((cr) => (
            <div key={cr.id} className="flex items-start gap-3 rounded-xl border border-border px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">{cr.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{cr.content}</p>
              </div>
              <button onClick={() => handleDelete(cr.id)}
                className="shrink-0 text-muted-foreground hover:text-destructive transition-colors cursor-pointer">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ticket detail page ─────────────────────────────────────────

export function AdminSupportDetailPage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const threadRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [ticket, setTicket]         = useState<TicketDetail | null>(null);
  const [loading, setLoading]       = useState(true);
  const [priority, setPriority]     = useState("normal");
  const [category, setCategory]     = useState("general");
  const [replyContent, setReplyContent] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [sending, setSending]       = useState(false);
  const [sendError, setSendError]   = useState("");
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [cannedResponses, setCannedResponses] = useState<Array<{ id: string; title: string; content: string }>>([]);
  const [showCannedDropdown, setShowCannedDropdown] = useState(false);
  const MAX_ATTACHMENTS = 2;

  useEffect(() => {
    if (!id) return;
    adminSupportApi.get<TicketDetail>(`/${id}`)
      .then((res) => {
        if (res.data) {
          setTicket(res.data);
          setPriority(res.data.priority);
          setCategory(res.data.category);
          adminSupportApi.post(`/${id}/read`).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    adminSupportApi.listCanned().then((r) => setCannedResponses(r.data ?? [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [ticket?.messages.length]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id || !ticket) return;
    try {
      await adminSupportApi.patch(`/${id}`, { status: newStatus });
      setTicket((p) => p ? { ...p, status: newStatus } : p);
    } catch { /* ignore */ }
  };

  const handlePriorityChange = (v: string) => setPriority(v);
  const handleCategoryChange = (v: string) => setCategory(v);

  const handleMetaSave = async () => {
    if (!id || !ticket || savingMeta) return;
    setSavingMeta(true);
    try {
      await adminSupportApi.patch(`/${id}`, { priority, category });
      setTicket((p) => p ? { ...p, priority, category } : p);
    } catch { /* ignore */ }
    finally { setSavingMeta(false); }
  };

  const handleSend = async () => {
    if (!id || !ticket || !replyContent.trim() || sending) return;
    setSending(true); setSendError("");
    try {
      let uploadedKeys: string[] = [];
      if (pendingFiles.length > 0 && !isInternal)
        uploadedKeys = await adminSupportApi.uploadAttachments(id, pendingFiles);
      const res = await adminSupportApi.post<{ id: string; created_at: string }>(`/${id}/messages`, {
        content: replyContent.trim(), isInternal, attachmentKeys: uploadedKeys,
      });
      if (res.data) {
        const msg: AdminSupportMessage = {
          id: res.data.id, author_type: "admin",
          content: replyContent.trim(), is_internal: isInternal ? 1 : 0,
          attachment_keys: uploadedKeys.length ? JSON.stringify(uploadedKeys) : null,
          created_at: res.data.created_at,
        };
        setTicket((p) => p ? { ...p, messages: [...p.messages, msg] } : p);
        setReplyContent(""); setPendingFiles([]); setIsInternal(false);
      }
    } catch (err) { setSendError(err instanceof Error ? err.message : t("common.unknownError")); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!ticket) return <div className="p-6 text-muted-foreground">{t("common.error")}</div>;

  const organizerLabel = ticket.organizer_name ?? ticket.organizer_email;
  const isTerminal = ticket.status === "resolved" || ticket.status === "closed";

  return (
    <>
      {/* Back */}
      <AppLink to={paths.support} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Destek Talepleri
      </AppLink>

      {/* Subject */}
      <h1 className="text-2xl font-bold text-foreground mb-6">{ticket.subject}</h1>

      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_264px] gap-5 items-start">

        {/* ── Left: Thread + Reply ── */}
        <div className="flex flex-col gap-0 min-w-0">

          {/* Thread */}
          <div
            ref={threadRef}
            className="rounded-t-2xl border border-border border-b-0 bg-muted/10 overflow-y-auto overflow-x-hidden min-h-[200px] max-h-[55vh] px-4 py-5 space-y-1"
          >
            <AdminMessageBubble
              authorType="organizer" content={ticket.body} createdAt={ticket.created_at}
              label={organizerLabel} isInternal={0} attachmentKeys={null} ticketId={ticket.id}
            />
            {ticket.messages.map((msg) => (
              <AdminMessageBubble key={msg.id}
                authorType={msg.author_type} content={msg.content}
                createdAt={msg.created_at}
                label={msg.author_type === "organizer" ? organizerLabel : t("support.you")}
                isInternal={msg.is_internal} attachmentKeys={msg.attachment_keys} ticketId={ticket.id}
              />
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Reply box */}
          {isTerminal ? (
            <div className="rounded-b-2xl border border-border bg-muted/30 px-5 py-4 text-center">
              <p className="text-xs text-muted-foreground">Bu talep kapatıldı. Yeni mesaj gönderilemez.</p>
            </div>
          ) : (
            <div className={cn(
              "rounded-b-2xl border border-border bg-card overflow-hidden",
              isInternal && "border-amber-500/30"
            )}>
              {/* Tab bar */}
              <div className="flex border-b border-border">
                <button
                  onClick={() => setIsInternal(false)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-xs font-semibold transition-colors cursor-pointer",
                    !isInternal
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Send className="h-3.5 w-3.5" /> Yanıtla
                </button>
                <button
                  onClick={() => setIsInternal(true)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-xs font-semibold transition-colors cursor-pointer",
                    isInternal
                      ? "text-amber-500 border-b-2 border-amber-500 bg-amber-500/5"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <span className="text-base leading-none">🔒</span> Dahili Not
                </button>
                {/* Canned responses */}
                {cannedResponses.length > 0 && (
                  <div className="relative ml-auto flex items-center pr-3">
                    <button onClick={() => setShowCannedDropdown((v) => !v)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-2 py-1 rounded hover:bg-muted">
                      Hazır yanıt ▾
                    </button>
                    {showCannedDropdown && (
                      <div className="absolute right-0 top-8 z-20 w-80 max-h-60 overflow-y-auto rounded-2xl border border-border bg-card shadow-xl">
                        {cannedResponses.map((cr) => (
                          <button key={cr.id}
                            onClick={() => { setReplyContent((p) => p + (p ? "\n\n" : "") + cr.content); setShowCannedDropdown(false); }}
                            className="w-full px-4 py-3 text-left hover:bg-primary/5 transition-colors border-b border-border last:border-0">
                            <p className="text-sm font-medium text-foreground">{cr.title}</p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">{cr.content}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Textarea */}
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={4}
                placeholder={isInternal ? "Dahili not ekle (organizatöre görünmez)…" : t("adminSupport.replyPlaceholder")}
                disabled={sending}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend(); }}
                className={cn(
                  "w-full resize-none px-4 py-3 text-sm bg-transparent focus:outline-none disabled:opacity-50 placeholder:text-muted-foreground/40",
                  isInternal && "text-amber-700 dark:text-amber-300"
                )}
              />

              {/* Footer toolbar */}
              <div className="flex items-center gap-2 px-4 pb-3 border-t border-border pt-3">
                {!isInternal && (
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                    <Paperclip className="h-4 w-4" />
                    <span>{pendingFiles.length > 0 ? `${pendingFiles.length} dosya` : "Ekle"}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="sr-only"
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files ?? []).slice(0, MAX_ATTACHMENTS - pendingFiles.length);
                        setPendingFiles((p) => [...p, ...newFiles].slice(0, MAX_ATTACHMENTS));
                        e.target.value = "";
                      }} />
                  </label>
                )}
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs bg-muted">
                    <span className="truncate max-w-[80px]">{f.name}</span>
                    <button onClick={() => setPendingFiles((p) => p.filter((_, idx) => idx !== i))}
                      className="text-muted-foreground hover:text-destructive cursor-pointer">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {sendError && <p className="text-xs text-destructive">{sendError}</p>}
                <p className="text-[10px] text-muted-foreground/50 ml-auto mr-2 hidden sm:block">Ctrl+Enter gönderir</p>
                <button onClick={handleSend} disabled={!replyContent.trim() || sending}
                  className={cn(
                    "flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-semibold transition-all cursor-pointer shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                    isInternal
                      ? "bg-amber-500 hover:bg-amber-600 shadow-amber-500/20"
                      : "bg-primary hover:bg-primary/90 shadow-primary/20"
                  )}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? "Gönderiliyor…" : isInternal ? "Not Ekle" : "Gönder"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Sidebar ── */}
        <div className="flex flex-col gap-3">

          {/* Organizer card */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Organizatör</p>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center shrink-0">
                {organizerLabel[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{organizerLabel}</p>
                <p className="text-xs text-muted-foreground truncate">{ticket.organizer_email}</p>
                {ticket.wedding_slug && (
                  <span className="inline-block font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded mt-1">/{ticket.wedding_slug}</span>
                )}
              </div>
            </div>
          </div>

          {/* Ticket info */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Detaylar</p>
              <span className="font-mono text-[10px] text-muted-foreground/60">{formatTicketNumber(ticket.ticket_number)}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={ticket.status} />
              {ticket.csat_score !== null && (
                <span className="text-xs text-amber-400">{"★".repeat(ticket.csat_score)}{"☆".repeat(5 - ticket.csat_score)}</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {new Date(ticket.created_at + "Z").toLocaleString()}
            </div>

            <div className="pt-2 border-t border-border space-y-3">
              {/* Priority */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Öncelik</p>
                {isTerminal ? <PriorityBadge priority={ticket.priority} /> : (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(PRIORITY_CFG).map(([v, cfg]) => (
                      <button key={v} onClick={() => handlePriorityChange(v)}
                        className={cn(
                          "px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border transition-all cursor-pointer",
                          priority === v ? cn("border-2", cfg.cls) : "border-border text-muted-foreground hover:border-primary/20"
                        )}>
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Category */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Kategori</p>
                {isTerminal ? (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">{CATEGORY_CFG[ticket.category] ?? ticket.category}</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(CATEGORY_CFG).map(([v, label]) => (
                      <button key={v} onClick={() => handleCategoryChange(v)}
                        className={cn(
                          "px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer",
                          category === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/20"
                        )}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

            {/* Save meta button */}
            {!isTerminal && (priority !== ticket.priority || category !== ticket.category) && (
              <button
                onClick={handleMetaSave}
                disabled={savingMeta}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer shadow-sm shadow-primary/20 transition-colors"
              >
                {savingMeta ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Değişiklikleri Kaydet
              </button>
            )}
            </div>
          </div>

          {/* Status actions */}
          {!isTerminal && (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Aksiyonlar</p>
              {ticket.status === "open" && (
                <button onClick={() => handleStatusChange("in_review")}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all cursor-pointer">
                  İncelemeye Al
                </button>
              )}
              <button onClick={() => setShowResolveModal(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500 text-xs font-semibold hover:bg-green-500/20 transition-all cursor-pointer">
                <CheckCircle className="h-3.5 w-3.5" /> Çözüldü Olarak Kapat
              </button>
            </div>
          )}
          {ticket.status === "resolved" && (
            <div className="rounded-2xl border border-border bg-card p-4">
              <button onClick={() => handleStatusChange("closed")}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-all cursor-pointer">
                Tamamen Kapat
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Resolve modal */}
      {showResolveModal && (
        <SendResolveModal ticketId={ticket.id} ticketNumber={ticket.ticket_number}
          onClose={() => setShowResolveModal(false)}
          onResolved={() => {
            setShowResolveModal(false);
            setTicket((p) => p ? { ...p, status: "resolved" } : p);
          }} />
      )}
    </>
  );
}

// ── Resolve modal ──────────────────────────────────────────────

function SendResolveModal({ ticketId, ticketNumber, onClose, onResolved }: {
  ticketId: string; ticketNumber: number; onClose: () => void; onResolved: () => void;
}) {
  const { t } = useTranslation();
  const [content, setContent]           = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState("");
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleResolve = async () => {
    setSubmitting(true); setError("");
    try { await adminSupportApi.resolve(ticketId, content.trim() || undefined, sendNotification); onResolved(); }
    catch (err) { setError(err instanceof Error ? err.message : t("common.unknownError")); }
    finally { setSubmitting(false); }
  };

  return (
    <div ref={backdropRef} onClick={(e) => e.target === backdropRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold">{t("support.sendAndResolve")} — {formatTicketNumber(ticketNumber)}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4}
            placeholder={`${t("adminSupport.replyPlaceholder")} (${t("common.optional")})`}
            className="w-full resize-none bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-xl px-3 py-2.5 text-sm transition-[border-color,box-shadow]" />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sendNotification} onChange={(e) => setSendNotification(e.target.checked)}
              className="rounded border-border text-primary cursor-pointer" />
            <span className="text-muted-foreground">{t("support.sendNotification")}</span>
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors cursor-pointer">{t("common.cancel")}</button>
          <button onClick={handleResolve} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 cursor-pointer shadow-sm">
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("support.sendAndResolve")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Message bubble ─────────────────────────────────────────────

function AdminMessageBubble({ authorType, content, createdAt, label, isInternal, attachmentKeys, ticketId }: {
  authorType: "organizer" | "admin"; content: string; createdAt: string;
  label: string; isInternal: number; attachmentKeys: string | null;
  ticketId: string; isFirst?: boolean;
}) {
  const isAdmin = authorType === "admin";
  const internal = isInternal === 1;
  const parsedKeys: string[] = attachmentKeys
    ? (() => { try { return JSON.parse(attachmentKeys); } catch { return []; } })()
    : [];
  const time = new Date(createdAt + (createdAt.includes("Z") ? "" : "Z"))
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Internal note
  if (internal) {
    return (
      <div className="flex items-end gap-2 flex-row-reverse py-1">
        <div className="h-7 w-7 rounded-full bg-amber-500/20 text-amber-500 text-[11px] font-bold flex items-center justify-center shrink-0">
          {label[0]?.toUpperCase()}
        </div>
        <div className="max-w-[75%] bg-amber-500/10 border border-amber-500/20 rounded-2xl rounded-tr-none px-4 py-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500">🔒 Dahili Not</span>
            <span className="text-[10px] text-amber-500/50 ml-auto">{time}</span>
          </div>
          <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed break-words italic">{content}</p>
        </div>
      </div>
    );
  }

  // Admin message (right)
  if (isAdmin) {
    return (
      <div className="flex items-end gap-2 flex-row-reverse py-1">
        <div className="h-7 w-7 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0 ring-2 ring-primary/20">
          {label[0]?.toUpperCase()}
        </div>
        <div className="max-w-[75%]">
          <div className="bg-primary rounded-2xl rounded-tr-none px-4 py-2.5 shadow-md shadow-primary/20">
            <p className="text-sm text-white leading-relaxed break-words">{content}</p>
            {parsedKeys.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {parsedKeys.map((key) => (
                  <a key={key} href={adminSupportApi.attachmentUrl(ticketId, key)} target="_blank" rel="noopener noreferrer">
                    <img src={adminSupportApi.attachmentUrl(ticketId, key)} alt="ek" className="max-h-32 rounded-lg object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground/50 text-right mt-1 mr-1">{time}</p>
        </div>
      </div>
    );
  }

  // Organizer message (left)
  return (
    <div className="flex items-end gap-2 py-1">
      <div className="h-7 w-7 rounded-full bg-muted text-muted-foreground text-[11px] font-bold flex items-center justify-center shrink-0">
        {label[0]?.toUpperCase()}
      </div>
      <div className="max-w-[75%]">
        <div className="bg-card border border-border rounded-2xl rounded-tl-none px-4 py-2.5">
          <p className="text-sm text-foreground leading-relaxed break-words">{content}</p>
          {parsedKeys.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {parsedKeys.map((key) => (
                <a key={key} href={adminSupportApi.attachmentUrl(ticketId, key)} target="_blank" rel="noopener noreferrer">
                  <img src={adminSupportApi.attachmentUrl(ticketId, key)} alt="ek" className="max-h-32 rounded-lg object-cover border border-border" />
                </a>
              ))}
            </div>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground/50 mt-1 ml-1">{time}</p>
      </div>
    </div>
  );
}
